import React, { useEffect, useState } from "react";
import { getReminderAccordingtoDate } from "./services/services";
import * as XLSX from "xlsx";

const ReminderList = () => {
  const today = new Date().toISOString().split("T")[0];

  const [selectedDate, setSelectedDate] = useState(today);
  const [dateInput, setDateInput] = useState(today);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [clinicFilter, setClinicFilter] = useState(""); // ✅ new state for clinic

  useEffect(() => {
    const Payload = {
      data: { filter: "" },
      page: 0,
      pageSize: 10000,
      order: [["createdAt", "ASC"]],
    };

    setLoading(true);
    getReminderAccordingtoDate(Payload)
      .then((res) => {
        setReminders(res?.data?.data?.rows || []);
      })
      .catch((err) => {
        console.error("Error fetching reminders:", err);
        setReminders([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleDateChange = (e) => {
    const date = e.target.value;
    setDateInput(date);
    setSelectedDate(date);
  };

  // ✅ Apply search + reason + clinic + date filter
  const filteredReminders = reminders.filter((r) => {
    const searchLower = search.toLowerCase();

    const matchesSearch =
      r.patientName?.toLowerCase().includes(searchLower) ||
      r.patientNumber?.includes(searchLower) ||
      r.disease?.toLowerCase().includes(searchLower) ||
      r.address?.toLowerCase().includes(searchLower);

    const matchesReason = reasonFilter ? r.reason === reasonFilter : true;

    const matchesClinic = clinicFilter
      ? r.fromWhere?.toLowerCase() === clinicFilter.toLowerCase()
      : true;

    const visits = Array.isArray(r.nextVisits) ? r.nextVisits : [];
    const matchesDate =
      visits.length > 0 && visits.some((d) => d.split("T")[0] === selectedDate);

    return matchesSearch && matchesReason && matchesClinic && matchesDate;
  });

  // ✅ Export filtered data to Excel
  const exportToExcel = () => {
    const dataForExcel = filteredReminders.map((r) => {
      const visits = Array.isArray(r.nextVisits) ? r.nextVisits : [];
      const nextVisits =
        visits.length > 0 ? visits.map((d) => d.split("T")[0]).join(", ") : "-";

      return {
        ID: r.id,
        Name: r.patientName,
        Phone: r.patientNumber,
        Age: r.Age,
        Address: r.address,
        "Disease / Concern": r.disease,
        From: r.fromWhere,
        Reason:
          r.reason === "opd"
            ? "OPD"
            : r.reason === "operation"
            ? "Operation"
            : "Revisit",
        "Created At": r.createdAt?.split("T")[0],
        "Next Visits": nextVisits,
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reminders");
    XLSX.writeFile(workbook, `reminders_${dateInput}.xlsx`);
  };

  return (
    <div
      className="p-4"
      style={{
        background: "linear-gradient(135deg, #f8fbff, #e0f7fa)",
        minHeight: "100vh",
      }}
    >
      <h3 className="text-primary fw-bold mb-4">📅 Check Patient Reminders</h3>

      {/* Filters */}
      <div className="row mb-4 g-3">
        <div className="col-md-3">
          <label className="form-label fw-semibold">Select Date</label>
          <input
            type="date"
            className="form-control form-control-lg"
            value={dateInput}
            onChange={handleDateChange}
          />
        </div>

        <div className="col-md-4">
          <label className="form-label fw-semibold">Search</label>
          <input
            type="text"
            className="form-control form-control-lg"
            placeholder="🔍 Search by Place, Name, Phone, or Disease"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold">Filter by Reason</label>
          <select
            className="form-select form-select-lg"
            value={reasonFilter}
            onChange={(e) => setReasonFilter(e.target.value)}
          >
            <option value="">All</option>
            <option value="opd">OPD</option>
            <option value="operation">Operation</option>
            <option value="revisit">Revisit</option>
            <option value="surgerypending">Surgery Pending</option>
          </select>
        </div>

        <div className="col-md-2">
          <label className="form-label fw-semibold">Filter by Clinic</label>
          <select
            className="form-select form-select-lg"
            value={clinicFilter}
            onChange={(e) => setClinicFilter(e.target.value)}
          >
            <option value="">All Clinics</option>
            <option value="clinic">Clinic</option>
            <option value="sum">SUM</option>
            <option value="kims">KIMS</option>
          </select>
        </div>

        <div className="col-md-1 d-flex align-items-end">
          <button className="btn btn-primary w-100" onClick={exportToExcel}>
            📥 Export
          </button>
        </div>
      </div>

      {/* Reminders Table */}
      <div className="card shadow-lg border-0 rounded-4 p-3">
        <h5 className="text-secondary fw-bold mb-3">
          Reminders for {dateInput}:
        </h5>

        {loading ? (
          <div className="d-flex justify-content-center p-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredReminders.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle">
              <thead className="table-light">
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Age</th>
                  <th>Address</th>
                  <th>Disease / Concern</th>
                  <th>From</th>
                  <th>Reason</th>
                  <th>Created At</th>
                  <th>Next Visits</th>
                </tr>
              </thead>
              <tbody>
                {filteredReminders.map((r, i) => {
                  const visits = Array.isArray(r.nextVisits)
                    ? r.nextVisits
                    : [];

                  return (
                    <tr key={i}>
                      <td>{r.id}</td>
                      <td>{r.patientName}</td>
                      <td>{r.patientNumber}</td>
                      <td>{r.Age}</td>
                      <td>{r.address}</td>
                      <td>{r.disease}</td>
                      <td>{r.fromWhere}</td>
                      <td>
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "#0d6efd",
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "12px",
                            fontSize: "0.9rem",
                          }}
                        >
                          {r.reason === "opd"
                            ? "OPD"
                            : r.reason === "operation"
                            ? "Operation"
                            : "Revisit"}
                        </span>
                      </td>
                      <td>{r.createdAt?.split("T")[0]}</td>
                      <td>
                        {visits.length > 0
                          ? visits.map((d, idx) => (
                              <span
                                key={idx}
                                className="badge bg-info text-dark me-1"
                              >
                                {d.split("T")[0]}
                              </span>
                            ))
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted">No reminders found.</p>
        )}
      </div>
    </div>
  );
};

export default ReminderList;
